import  { useCallback, useEffect, useState } from 'react';
import { Stethoscope, Syringe } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import '../index.css';

// Datos de testimonios
const testimonials = [
  {
    name: 'Dra. Laura Méndez',
    image: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
    text: 'La calidad de los equipos quirúrgicos ha elevado nuestros estándares de atención. Proveedor confiable y profesional.',
    area: 'Cirugía General',
  },
  {
    name: 'Dr. Carlos Ramírez',
    image: 'https://images.unsplash.com/photo-1551601651-2a8555f1a136?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
    text: 'Suministros médicos esterilizados con entrega inmediata. Fundamental para nuestro trabajo diario en emergencias.',
    area: 'Urgencias Médicas',
  },
  {
    name: 'Clinica Santa María',
    image: 'https://images.unsplash.com/photo-1584432810601-6c7f27d2362b?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
    text: 'Equipamiento de última generación que cumple con todas las normativas internacionales. Soporte técnico excepcional.',
    area: 'Diagnóstico por Imágenes',
  },
  {
    name: 'Dr. Fernando Gómez',
    image: 'https://images.unsplash.com/photo-1582750433449-648ed127bb54?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
    text: 'Insumos médicos de calidad superior que garantizan la seguridad de nuestros pacientes en cada procedimiento.',
    area: 'Cardiología',
  },
];

// Componente para los puntos de navegación
const NavigationDots = ({ testimonials, selectedIndex, scrollTo }) => (
  <div className="flex justify-center mt-8 space-x-2">
    {testimonials.map((_, index) => (
      <button
        key={index}
        onClick={() => scrollTo(index)}
        className={`w-3 h-3 rounded-full transition-all duration-300 ${
          selectedIndex === index ? 'bg-accent w-8' : 'bg-primary/30'
        }`}
        aria-label={`Navegar a testimonio ${index + 1}`}
      />
    ))}
  </div>
);

// Componente para las flechas de navegación
const NavigationArrows = ({ emblaApi }) => (
  <div className="hidden md:flex justify-between items-center absolute top-1/2 left-0 right-0 -translate-y-1/2 px-4">
    <button
      onClick={() => emblaApi?.scrollPrev()}
      className="p-3 rounded-full bg-section-bg shadow-lg hover:shadow-xl transition-shadow"
      aria-label="Testimonio anterior"
    >
      <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
      </svg>
    </button>
    <button
      onClick={() => emblaApi?.scrollNext()}
      className="p-3 rounded-full bg-section-bg shadow-lg hover:shadow-xl transition-shadow"
      aria-label="Siguiente testimonio"
    >
      <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </button>
  </div>
);

export const Testimonials = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'center' }, [Autoplay({ delay: 5000 })]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Actualiza el índice activo
  const updateIndex = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  // Configura el listener para el evento 'select'
  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on('select', updateIndex);
    return () => emblaApi.off('select', updateIndex);
  }, [emblaApi, updateIndex]);

  return (
    <div className="py-24 bg-main-bg relative overflow-hidden">
      {/* Elementos decorativos */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-secondary/10 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4">
        {/* Encabezado */}
        <div className="text-center mb-16">
          <h2 className="text-2xl md:text-4xl font-bold text-primary-dark mb-4 font-montserrat">
            Confiado por Profesionales de la Salud
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto font-poppins">
            Instituciones médicas líderes respaldan nuestra calidad y servicio
          </p>
        </div>

        {/* Carrusel */}
        <div className="embla relative" ref={emblaRef}>
          <div className="embla__container flex">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="embla__slide flex-[0_0_80%] md:flex-[0_0_40%] lg:flex-[0_0_30%] px-4"
              >
                <div
                  className={`relative bg-section-bg p-8 rounded-2xl shadow-lg transition-all duration-300 
                    ${selectedIndex === index ? 'scale-105 opacity-100 border-2 border-accent' : 'scale-95 opacity-90 border border-gray-200'}`}
                >
                  <div className="absolute top-6 left-6 -rotate-12 opacity-20">
                    <Syringe className="h-16 w-16 text-accent" />
                  </div>

                  <div className="flex items-start mb-6">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-16 h-16 rounded-full object-cover border-4 border-primary mr-4 hover:rotate-3 transition-transform duration-300"
                    />
                    <div>
                      <h3 className="font-semibold text-primary-dark text-xl font-montserrat">{testimonial.name}</h3>
                      <p className="text-accent text-sm font-medium font-poppins">{testimonial.area}</p>
                    </div>
                  </div>

                  <p className="text-gray-600 text-lg leading-relaxed relative z-10 font-poppins">
                    "{testimonial.text}"
                  </p>

                  <div className="absolute bottom-6 right-6 opacity-10">
                    <Stethoscope className="h-16 w-16 text-accent" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Puntos de navegación */}
          <NavigationDots
            testimonials={testimonials}
            selectedIndex={selectedIndex}
            scrollTo={(index) => emblaApi?.scrollTo(index)}
          />
        </div>

        {/* Flechas de navegación */}
        <NavigationArrows emblaApi={emblaApi} />
      </div>
    </div>
  );
};